import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import notificationService from '../../services/notificationService';

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchList',
  async ({ cursor, limit, category } = {}) => {
    const response = await notificationService.getNotifications(cursor, limit, category);
    return response.data.data;
  }
);

// Circuit breaker: stop polling after consecutive failures
let unreadFailCount = 0;
const MAX_UNREAD_FAILURES = 2;

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    if (unreadFailCount >= MAX_UNREAD_FAILURES) {
      return rejectWithValue('service_unavailable');
    }
    try {
      const response = await notificationService.getUnreadCount();
      unreadFailCount = 0;
      return response.data?.data?.count || 0;
    } catch (err) {
      unreadFailCount += 1;
      const status = err.response?.status;
      if (status === 502 || status === 503 || status === 404) {
        return rejectWithValue('service_unavailable');
      }
      return rejectWithValue(err.message);
    }
  }
);

export const markAsRead = createAsyncThunk(
  'notifications/markRead',
  async (notificationId) => {
    await notificationService.markRead(notificationId);
    return notificationId;
  }
);

export const markAllAsRead = createAsyncThunk(
  'notifications/markAllRead',
  async () => {
    await notificationService.markAllRead();
  }
);

export const fetchPreferences = createAsyncThunk(
  'notifications/fetchPreferences',
  async () => {
    const response = await notificationService.getPreferences();
    return response.data.data;
  }
);

export const updatePreferences = createAsyncThunk(
  'notifications/updatePreferences',
  async (prefs) => {
    await notificationService.updatePreferences(prefs);
    return prefs;
  }
);

export const registerDeviceToken = createAsyncThunk(
  'notifications/registerToken',
  async ({ token, platform }) => {
    await notificationService.registerDeviceToken(token, platform);
    return { token, platform };
  }
);

export const unregisterDeviceToken = createAsyncThunk(
  'notifications/unregisterToken',
  async (token, { rejectWithValue }) => {
    try {
      await notificationService.unregisterDeviceToken(token);
      return true;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,
  nextCursor: null,
  hasMore: true,
  loading: false,
  preferences: null,
  preferencesLoading: false,
  toast: null,
  dropdownOpen: false,
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action) => {
      state.notifications.unshift(action.payload);
      state.unreadCount += 1;
    },
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
    incrementUnreadCount: (state) => {
      state.unreadCount += 1;
    },
    showToast: (state, action) => {
      state.toast = action.payload;
    },
    dismissToast: (state) => {
      state.toast = null;
    },
    toggleDropdown: (state) => {
      state.dropdownOpen = !state.dropdownOpen;
    },
    closeDropdown: (state) => {
      state.dropdownOpen = false;
    },
    resetNotifications: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => { state.loading = true; })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const { notifications, next_cursor, has_more } = action.payload;
        state.notifications = [...state.notifications, ...notifications];
        state.nextCursor = next_cursor;
        state.hasMore = has_more;
        state.loading = false;
      })
      .addCase(fetchNotifications.rejected, (state) => { state.loading = false; })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(markAsRead.fulfilled, (state, action) => {
        const notif = state.notifications.find(n => n.id === action.payload);
        if (notif && !notif.is_read) {
          notif.is_read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.notifications.forEach(n => { n.is_read = true; });
        state.unreadCount = 0;
      })
      .addCase(fetchPreferences.pending, (state) => { state.preferencesLoading = true; })
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
        state.preferencesLoading = false;
      })
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.preferences = { ...state.preferences, ...action.payload };
      });
  },
});

export const {
  addNotification, setUnreadCount, incrementUnreadCount, showToast,
  dismissToast, toggleDropdown, closeDropdown, resetNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;
